import { Button, TooltipTrigger } from '@toggle/design-system';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { useScheduleDrawerUrlSync } from '~/hooks/use-schedule-drawer-url-sync/useScheduleDrawerUrlSync';
import { useListScheduledQueries } from '~/react-query-hooks/alfred/useScheduledQueries';
import { useFlags } from '~/stores/use-flags/useFlags';
import { MAX_SCHEDULED_TASKS } from '~/views/alfred/research/history-sidebar/HistorySidebar';
import { useChat } from '~/views/alfred/research/use-chat/useChat';

import { ChatInputConfig } from '../../chat-input';
import chatInputComposerStyles from '../../chat-input/chat-input-composer/ChatInputComposer.module.scss';

export interface ChatSubmitButtonProps {
  isMessagePending: boolean;
  inputValue: string;
  abortRequest?: () => void;
  config?: ChatInputConfig;
  showRefineButton?: boolean;
  onRefineClick?: () => void;
  usageIndicator?: ReactNode;
}

export const ChatSubmitButton = ({
  isMessagePending,
  inputValue,
  abortRequest,
  config,
  showRefineButton = false,
  onRefineClick,
  usageIndicator,
}: ChatSubmitButtonProps) => {
  const { t } = useTranslation('chat');
  const isAlfredEverywhere = config?.alfredEverywhere;
  const { open: openScheduleDrawer } = useScheduleDrawerUrlSync();
  const webSearch = useChat(state => state.webSearch);

  const isScheduledTaskEnabled = useFlags(state =>
    state.getFeatureFlagState('scheduled_task_feature')
  );

  const { data: scheduledQueriesData, isLoading: isScheduledQueriesLoading } =
    useListScheduledQueries({ status: 'active' }, isScheduledTaskEnabled);
  // Treat an unknown count (still loading) as at the limit to avoid a first-load
  // window where the button is enabled for users already at the cap.
  const isAtMaxScheduledTasks =
    isScheduledTaskEnabled &&
    (isScheduledQueriesLoading ||
      (scheduledQueriesData?.total_count ?? 0) >= MAX_SCHEDULED_TASKS);

  const hasEmptyInput = !inputValue.trim();
  const isChatInputDisabled = config?.isCustomizedDisabled === true;
  const isSubmitButtonDisabled =
    hasEmptyInput ||
    isChatInputDisabled ||
    (isAlfredEverywhere && isMessagePending);
  const isScheduleButtonDisabled = hasEmptyInput || isAtMaxScheduledTasks;

  const handleScheduleClick = () => {
    openScheduleDrawer(inputValue.trim(), webSearch);
  };

  const getScheduleTooltipLabel = () => {
    if (isAtMaxScheduledTasks) {
      return t('chat:historySidebar.scheduleTaskDisabledMaxReached');
    }

    if (hasEmptyInput) {
      return t('chat:scheduleTaskDisabledNoInput');
    }

    return t('chat:historySidebar.scheduleTask');
  };

  const scheduleButton = isScheduledTaskEnabled && (
    <TooltipContainer
      label={getScheduleTooltipLabel()}
      inPortal
      trigger={[TooltipTrigger.Hover]}
    >
      <Button
        className={chatInputComposerStyles.styledSubmitButton}
        data-testid="schedule-task-button"
        type="button"
        variant="tertiary"
        iconName="Clock"
        aria-label={t('chat:historySidebar.scheduleTask')}
        disabled={isScheduleButtonDisabled}
        onClick={handleScheduleClick}
      />
    </TooltipContainer>
  );

  const refineButton = showRefineButton && (
    <Button
      className={chatInputComposerStyles.refineButton}
      type="button"
      variant="empty"
      size="medium"
      iconName="Alfred"
      label={t('chat:promptRefinement.button')}
      onClick={onRefineClick}
      disabled={isSubmitButtonDisabled}
      data-testid="prompt-refinement-button"
    />
  );

  const submitButton = (
    <TooltipContainer
      label={t('chat:stopResponse')}
      inPortal
      trigger={[TooltipTrigger.Hover]}
      disabled={!isMessagePending}
    >
      {isMessagePending && !isAlfredEverywhere ? (
        <Button
          className={chatInputComposerStyles.styledSubmitButton}
          data-testid="abort-button"
          type="button"
          iconName="Stop"
          onClick={abortRequest}
        />
      ) : (
        <Button
          className={chatInputComposerStyles.styledSubmitButton}
          data-testid="submit-button"
          type="submit"
          variant="primary"
          iconName="ArrowUp"
          disabled={isSubmitButtonDisabled}
        />
      )}
    </TooltipContainer>
  );

  return (
    <div className={chatInputComposerStyles.actionButtons}>
      {refineButton}
      {usageIndicator}
      {scheduleButton}
      {submitButton}
    </div>
  );
};
